// src/pages/creator-panel/LiveConversation/Tools/ToolForm.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { useChatbots } from '../../../../contexts/ChatbotContext';
import { hasPermission } from '../../../../utils/permissionUtils';

import {
    Box, Typography, Button, Alert, TextField, Switch, FormControlLabel,
    Select, MenuItem, InputLabel, FormControl, CircularProgress,
    Card, CardContent, Divider, IconButton, Chip, Stack, Tabs, Tab, Paper,
    Tooltip, List, ListItemButton, ListItemText, LinearProgress,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as BackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Build as ToolIcon,
    Code as CodeIcon,
    Visibility as VisualIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const PARAM_TYPES = ['string', 'number', 'integer', 'boolean'];

const emptyHeader = () => ({ key: '', value: '' });
const emptyParam = () => ({ name: '', type: 'string', description: '', required: true });

const sluggify = (s) => (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);

export default function ToolForm() {
    const navigate = useNavigate();
    const { toolId, chatbotId: urlChatbotId } = useParams();
    const isEdit = !!toolId;

    const { activeChatbot } = useChatbots();
    const chatbotId = activeChatbot?.chatbot_identity;
    const scopedId = urlChatbotId || chatbotId;
    const baseToolsPath = `/chatbot/${scopedId}/tool-call`;

    const [canModify, setCanModify] = useState(false);
    const [checking, setChecking] = useState(true);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    // Core fields
    const [displayName, setDisplayName] = useState('');
    const [name, setName] = useState('');
    const [nameTouched, setNameTouched] = useState(false);
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [enabledVoice, setEnabledVoice] = useState(true);
    const [enabledText, setEnabledText] = useState(true);
    // Default OFF — existing tools must not auto-fire on real phone calls
    // until the creator explicitly opts them in.
    const [enabledPhone, setEnabledPhone] = useState(false);

    // HTTP
    const [httpMethod, setHttpMethod] = useState('GET');
    const [endpointUrl, setEndpointUrl] = useState('');
    const [headers, setHeaders] = useState([emptyHeader()]);
    const [bodyTemplate, setBodyTemplate] = useState('');
    const [queryParams, setQueryParams] = useState([emptyHeader()]);

    // Auth
    const [authType, setAuthType] = useState('none');
    const [authConfig, setAuthConfig] = useState({});
    const [authAlreadyConfigured, setAuthAlreadyConfigured] = useState(false);

    // Parameters
    const [paramMode, setParamMode] = useState('visual'); // 'visual' | 'json'
    const [paramList, setParamList] = useState([]);
    const [paramJson, setParamJson] = useState('{}');

    // Advanced
    const [responseExtractPath, setResponseExtractPath] = useState('');
    const [timeoutSeconds, setTimeoutSeconds] = useState(10);
    const [responseMaxChars, setResponseMaxChars] = useState(4000);

    // Sidebar: list of all tools for this chatbot
    const [allTools, setAllTools] = useState([]);
    const [toolsListLoading, setToolsListLoading] = useState(false);

    const resetForm = useCallback(() => {
        setDisplayName('');
        setName('');
        setNameTouched(false);
        setDescription('');
        setIsActive(true);
        setEnabledVoice(true);
        setEnabledText(true);
        setEnabledPhone(false);
        setHttpMethod('GET');
        setEndpointUrl('');
        setHeaders([emptyHeader()]);
        setBodyTemplate('');
        setQueryParams([emptyHeader()]);
        setAuthType('none');
        setAuthConfig({});
        setAuthAlreadyConfigured(false);
        setParamMode('visual');
        setParamList([]);
        setParamJson('{}');
        setResponseExtractPath('');
        setTimeoutSeconds(10);
        setResponseMaxChars(4000);
        setMessage(null);
    }, []);

    const fetchToolsList = useCallback(async () => {
        if (!chatbotId) return;
        setToolsListLoading(true);
        try {
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation/tools`);
            setAllTools(res.data?.data || []);
        } catch (e) {
            console.error('failed to load tools list', e);
        } finally {
            setToolsListLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        fetchToolsList();
    }, [fetchToolsList]);

    useEffect(() => {
        (async () => {
            setChecking(true);
            const modify = await hasPermission('live-conversation-tools', 'add|edit');
            setCanModify(modify);
            setChecking(false);
        })();
    }, []);

    useEffect(() => {
        if (!canModify) return;
        // Clear any prior tool's state first so switching between tools
        // (or to "new") never shows stale values.
        resetForm();
        if (!isEdit) return;
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get(`/api/live-conversation/tools/${toolId}`);
                const d = res.data?.data;
                if (!d) return;
                setDisplayName(d.display_name || '');
                setName(d.name || '');
                setNameTouched(true);
                setDescription(d.description || '');
                setIsActive(!!d.is_active);
                setEnabledVoice(!!d.enabled_for_voice);
                setEnabledText(!!d.enabled_for_text);
                setEnabledPhone(!!d.enabled_for_phone);
                setHttpMethod(d.http_method || 'GET');
                setEndpointUrl(d.endpoint_url || '');
                setHeaders(Object.entries(d.headers || {}).map(([k, v]) => ({ key: k, value: String(v) })).concat(emptyHeader()));
                setBodyTemplate(d.body_template || '');
                setQueryParams(Object.entries(d.query_params_template || {}).map(([k, v]) => ({ key: k, value: String(v) })).concat(emptyHeader()));
                setAuthType(d.auth_type || 'none');
                setAuthAlreadyConfigured(!!d.has_auth_configured);
                const schema = d.parameters_schema || {};
                setParamList(Object.entries(schema).map(([k, v]) => ({
                    name: k, type: v.type || 'string',
                    description: v.description || '', required: !!v.required,
                })));
                setParamJson(JSON.stringify(schema, null, 2));
                setResponseExtractPath(d.response_extract_path || '');
                setTimeoutSeconds(d.timeout_seconds || 10);
                setResponseMaxChars(d.response_max_chars || 4000);
            } catch (e) {
                setMessage({ type: 'error', text: 'Failed to load tool' });
            } finally {
                setLoading(false);
            }
        })();
    }, [isEdit, toolId, canModify, resetForm]);

    const displayNameHandler = (val) => {
        setDisplayName(val);
        if (!nameTouched) setName(sluggify(val));
    };

    const parametersPayload = useMemo(() => {
        if (paramMode === 'json') {
            try { return JSON.parse(paramJson || '{}'); } catch { return null; }
        }
        const out = {};
        paramList.forEach((p) => {
            if (!p.name) return;
            out[p.name] = {
                type: p.type,
                description: p.description || '',
                required: !!p.required,
            };
        });
        return out;
    }, [paramMode, paramJson, paramList]);

    const headersPayload = useMemo(() => {
        const out = {};
        headers.forEach((h) => { if (h.key) out[h.key] = h.value; });
        return out;
    }, [headers]);

    const queryParamsPayload = useMemo(() => {
        const out = {};
        queryParams.forEach((h) => { if (h.key) out[h.key] = h.value; });
        return out;
    }, [queryParams]);

    const handleSave = async () => {
        if (!canModify) return;

        if (!name || !description || !endpointUrl) {
            setMessage({ type: 'error', text: 'Function name, description, and endpoint URL are required.' });
            return;
        }
        if (parametersPayload === null) {
            setMessage({ type: 'error', text: 'Parameters JSON is invalid.' });
            return;
        }

        const payload = {
            name,
            display_name: displayName,
            description,
            is_active: isActive,
            enabled_for_voice: enabledVoice,
            enabled_for_text: enabledText,
            enabled_for_phone: enabledPhone,
            http_method: httpMethod,
            endpoint_url: endpointUrl,
            headers: headersPayload,
            body_template: bodyTemplate,
            query_params_template: queryParamsPayload,
            auth_type: authType,
            parameters_schema: parametersPayload,
            response_extract_path: responseExtractPath,
            timeout_seconds: Number(timeoutSeconds) || 10,
            response_max_chars: Number(responseMaxChars) || 4000,
        };

        // Only include auth_config if user actually entered/changed credentials
        if (authType !== 'none' && Object.keys(authConfig).length > 0) {
            payload.auth_config = authConfig;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await axiosInstance.put(`/api/live-conversation/tools/${toolId}`, payload);
                setMessage({ type: 'success', text: 'Tool updated' });
                fetchToolsList();
            } else {
                const res = await axiosInstance.post(
                    `/api/chatbots/${chatbotId}/live-conversation/tools`,
                    payload,
                );
                setMessage({ type: 'success', text: 'Tool created' });
                const newId = res?.data?.data?.id;
                fetchToolsList();
                if (newId) {
                    setTimeout(() => navigate(`${baseToolsPath}/${newId}/edit`), 400);
                } else {
                    setTimeout(() => navigate(baseToolsPath), 600);
                }
            }
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.message || 'Save failed' });
        } finally {
            setSaving(false);
        }
    };

    const addParam = () => setParamList((l) => [...l, emptyParam()]);
    const updateParam = (i, patch) => setParamList((l) => l.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
    const removeParam = (i) => setParamList((l) => l.filter((_, idx) => idx !== i));

    const addHeader = () => setHeaders((l) => [...l, emptyHeader()]);
    const updateHeader = (i, patch) => setHeaders((l) => l.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
    const removeHeader = (i) => setHeaders((l) => l.filter((_, idx) => idx !== i));

    const addQueryParam = () => setQueryParams((l) => [...l, emptyHeader()]);
    const updateQueryParam = (i, patch) => setQueryParams((l) => l.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
    const removeQueryParam = (i) => setQueryParams((l) => l.filter((_, idx) => idx !== i));

    if (checking) {
        return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    if (!canModify) {
        return <Box sx={{ p: 4 }}><Alert severity="warning">No permission to manage Tools.</Alert></Box>;
    }

    const selectedId = toolId ? String(toolId) : null;

    return (
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* LEFT: Tools list sidebar */}
            <Paper
                variant="outlined"
                sx={{
                    width: 260,
                    flexShrink: 0,
                    position: 'sticky',
                    top: 16,
                    maxHeight: 'calc(100vh - 100px)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Back to tools table">
                        <IconButton size="small" onClick={() => navigate(baseToolsPath)}>
                            <BackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                        Tools
                    </Typography>
                    <Tooltip title="Create new tool">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                                if (!isEdit) return;
                                navigate(`${baseToolsPath}/new`);
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Divider />
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {toolsListLoading && <LinearProgress />}
                    <List dense disablePadding>
                        <ListItemButton
                            selected={!isEdit}
                            onClick={() => {
                                if (!isEdit) return;
                                navigate(`${baseToolsPath}/new`);
                            }}
                        >
                            <AddIcon fontSize="small" sx={{ mr: 1 }} />
                            <ListItemText
                                primary="Create New Tool"
                                primaryTypographyProps={{
                                    fontSize: 14,
                                    fontWeight: !isEdit ? 600 : 400,
                                }}
                            />
                        </ListItemButton>
                        <Divider />
                        {allTools.length === 0 && !toolsListLoading && (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    No tools yet.
                                </Typography>
                            </Box>
                        )}
                        {allTools.map((t) => {
                            const isSelected = selectedId === String(t.id);
                            return (
                                <ListItemButton
                                    key={t.id}
                                    selected={isSelected}
                                    onClick={() => {
                                        if (isSelected) return;
                                        navigate(`${baseToolsPath}/${t.id}/edit`);
                                    }}
                                >
                                    <ListItemText
                                        primary={t.display_name || t.name}
                                        secondary={t.name}
                                        primaryTypographyProps={{
                                            fontSize: 14,
                                            fontWeight: isSelected ? 600 : 400,
                                            noWrap: true,
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: 11,
                                            fontFamily: 'monospace',
                                            noWrap: true,
                                        }}
                                    />
                                    {!t.is_active && (
                                        <Chip label="off" size="small" variant="outlined" sx={{ ml: 1 }} />
                                    )}
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Box>
            </Paper>

            {/* RIGHT: Edit / Create form */}
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 1100 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    <ToolIcon color="primary" />
                    <Typography variant="h5" fontWeight={600}>
                        {isEdit ? 'Edit Tool' : 'Create Tool'}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Box>

                {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

            {/* Tool Settings */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Tool Settings</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure the basic settings for this tool.
                    </Typography>

                    <TextField label="Display Name" fullWidth sx={{ mb: 2 }}
                        value={displayName} onChange={(e) => displayNameHandler(e.target.value)} />

                    <TextField label="Function Name" fullWidth sx={{ mb: 2 }}
                        helperText="Snake_case identifier the LLM uses. Auto-generated from display name."
                        value={name}
                        onChange={(e) => { setName(sluggify(e.target.value)); setNameTouched(true); }}
                        inputProps={{ maxLength: 100 }} />

                    <TextField label="Description *" fullWidth multiline minRows={3} sx={{ mb: 2 }}
                        helperText={`${description.length}/1000 · This is what the AI reads to decide when to call the tool. Be specific about what it does and when it should fire.`}
                        value={description}
                        onChange={(e) => setDescription(e.target.value.slice(0, 1000))} />

                    <Stack direction="row" spacing={3} flexWrap="wrap">
                        <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label="Active" />
                        <FormControlLabel control={<Switch checked={enabledVoice} onChange={(e) => setEnabledVoice(e.target.checked)} />} label="Enabled for Voice" />
                        <FormControlLabel control={<Switch checked={enabledText} onChange={(e) => setEnabledText(e.target.checked)} />} label="Enabled for Text Chat" />
                        <FormControlLabel control={<Switch checked={enabledPhone} onChange={(e) => setEnabledPhone(e.target.checked)} />} label="Enabled for Phone Calls" />
                    </Stack>
                </CardContent>
            </Card>

            {/* Server Settings */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Server Settings</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your API endpoint — method, URL, headers and body template. Use <code>{'{param_name}'}</code> placeholders
                        that will be filled in by the LLM from the parameters schema.
                    </Typography>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 110 }}>
                            <InputLabel>Method</InputLabel>
                            <Select value={httpMethod} label="Method" onChange={(e) => setHttpMethod(e.target.value)}>
                                {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField fullWidth label="Endpoint URL *" placeholder="https://api.example.com/orders/{order_id}"
                            value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} />
                    </Stack>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Headers</Typography>
                    {headers.map((h, i) => (
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} key={i}>
                            <TextField size="small" label="Key" value={h.key}
                                onChange={(e) => updateHeader(i, { key: e.target.value })} />
                            <TextField size="small" fullWidth label="Value" value={h.value}
                                onChange={(e) => updateHeader(i, { value: e.target.value })} />
                            <IconButton size="small" onClick={() => removeHeader(i)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={addHeader}>Add Header</Button>

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Query Parameters (for GET/URL)</Typography>
                    {queryParams.map((h, i) => (
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} key={i}>
                            <TextField size="small" label="Key" value={h.key}
                                onChange={(e) => updateQueryParam(i, { key: e.target.value })} />
                            <TextField size="small" fullWidth label="Value (use {param_name})" value={h.value}
                                onChange={(e) => updateQueryParam(i, { value: e.target.value })} />
                            <IconButton size="small" onClick={() => removeQueryParam(i)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={addQueryParam}>Add Query Param</Button>

                    {['POST', 'PUT', 'PATCH'].includes(httpMethod) && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Request Body (JSON)</Typography>
                            <TextField multiline minRows={4} fullWidth placeholder='{"order_id": "{order_id}"}'
                                value={bodyTemplate} onChange={(e) => setBodyTemplate(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Auth */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Authentication</Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Auth Type</InputLabel>
                        <Select value={authType} label="Auth Type" onChange={(e) => { setAuthType(e.target.value); setAuthConfig({}); }}>
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="bearer">Bearer Token</MenuItem>
                            <MenuItem value="api_key">API Key (Header)</MenuItem>
                            <MenuItem value="basic">Basic Auth</MenuItem>
                        </Select>
                    </FormControl>

                    {authAlreadyConfigured && (
                        <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
                            Auth credentials are stored encrypted. Re-enter them below to change; leave blank to keep existing.
                        </Alert>
                    )}

                    {authType === 'bearer' && (
                        <TextField label="Bearer Token" fullWidth type="password"
                            value={authConfig.token || ''}
                            onChange={(e) => setAuthConfig({ token: e.target.value })} />
                    )}
                    {authType === 'api_key' && (
                        <Stack direction="row" spacing={2}>
                            <TextField label="Header Name" sx={{ flex: 1 }} placeholder="X-API-Key"
                                value={authConfig.key_name || ''}
                                onChange={(e) => setAuthConfig((c) => ({ ...c, key_name: e.target.value }))} />
                            <TextField label="API Key Value" sx={{ flex: 2 }} type="password"
                                value={authConfig.key_value || ''}
                                onChange={(e) => setAuthConfig((c) => ({ ...c, key_value: e.target.value }))} />
                        </Stack>
                    )}
                    {authType === 'basic' && (
                        <Stack direction="row" spacing={2}>
                            <TextField label="Username" sx={{ flex: 1 }}
                                value={authConfig.username || ''}
                                onChange={(e) => setAuthConfig((c) => ({ ...c, username: e.target.value }))} />
                            <TextField label="Password" sx={{ flex: 1 }} type="password"
                                value={authConfig.password || ''}
                                onChange={(e) => setAuthConfig((c) => ({ ...c, password: e.target.value }))} />
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {/* Parameters */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>Parameters</Typography>
                        <Tabs value={paramMode} onChange={(_, v) => setParamMode(v)}>
                            <Tab value="visual" icon={<VisualIcon fontSize="small" />} iconPosition="start" label="Visual" />
                            <Tab value="json" icon={<CodeIcon fontSize="small" />} iconPosition="start" label="JSON" />
                        </Tabs>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Define the parameters the AI will fill in. The LLM uses the description to infer what to send.
                    </Typography>

                    {paramMode === 'visual' ? (
                        <>
                            {paramList.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    No parameters yet. Add one below.
                                </Typography>
                            )}
                            {paramList.map((p, i) => (
                                <Paper variant="outlined" sx={{ p: 2, mb: 1 }} key={i}>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        <TextField size="small" label="Name" value={p.name}
                                            onChange={(e) => updateParam(i, { name: sluggify(e.target.value) })} />
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel>Type</InputLabel>
                                            <Select label="Type" value={p.type}
                                                onChange={(e) => updateParam(i, { type: e.target.value })}>
                                                {PARAM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <FormControlLabel
                                            control={<Switch checked={p.required}
                                                onChange={(e) => updateParam(i, { required: e.target.checked })} />}
                                            label="Required" />
                                        <Box sx={{ flex: 1 }} />
                                        <IconButton size="small" onClick={() => removeParam(i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Stack>
                                    <TextField size="small" fullWidth label="Description" sx={{ mt: 1 }}
                                        helperText="Tell the AI what this parameter represents."
                                        value={p.description}
                                        onChange={(e) => updateParam(i, { description: e.target.value })} />
                                </Paper>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={addParam}>Add Parameter</Button>
                        </>
                    ) : (
                        <>
                            <TextField multiline minRows={8} fullWidth
                                value={paramJson} onChange={(e) => setParamJson(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                                helperText={'Example: {"order_id": {"type": "string", "description": "The order ID", "required": true}}'} />
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Advanced */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Advanced</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                        <TextField label="Response Extract Path (JSONPath)" sx={{ flex: 2, minWidth: 240 }}
                            placeholder="$.data.status"
                            helperText="Optional. Trim response before sending to LLM."
                            value={responseExtractPath}
                            onChange={(e) => setResponseExtractPath(e.target.value)} />
                        <TextField label="Timeout (s)" type="number" sx={{ width: 130 }}
                            value={timeoutSeconds} onChange={(e) => setTimeoutSeconds(e.target.value)} />
                        <TextField label="Response Max Chars" type="number" sx={{ width: 180 }}
                            value={responseMaxChars} onChange={(e) => setResponseMaxChars(e.target.value)} />
                    </Stack>
                </CardContent>
            </Card>

                <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
